
import Vue from "vue";
import CodeMirrorWrapper from "../3rdPartyWrappers/CodeMirrorWrapper.vue";
import { Component, Prop } from "vue-property-decorator";
import IconButton from "@/components/UI/IconButton.vue";
import CodeInputDialog from "@/components/Reusable/CustomInputs/CodeInput/CodeInputDialog.vue";
import CodeMirror, { Editor } from "codemirror";
import { Tag } from "@/types/tags";
import Icon from "@/components/UI/Icon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import CodeBoxButtonForCodeEditor from "@/components/Partners/CodeBoxButtonForCodeEditor.vue";

export type CodeInputLanguage =
  | "css"
  | "html"
  | "javascript"
  | "x-php"
  | "twig";

@Component({
  components: {
    Icon,
    CodeInputDialog,
    IconButton,
    CodeMirrorWrapper,
    NiceButton,
    CodeBoxButtonForCodeEditor
  }
})
export default class CodeInput extends Vue {
  @Prop()
  value!: string | null;

  @Prop()
  autofillOnEmpty!: string | null;

  @Prop()
  language!: CodeInputLanguage;

  @Prop({ default: true })
  inline!: boolean;

  @Prop({ default: true })
  fullscreen!: boolean;

  @Prop({ default: "" })
  placeholder!: string;

  @Prop({
    default() {
      return [];
    }
  })
  tags!: Tag[];

  @Prop({
    default() {
      return [];
    }
  })
  twigVariables!: string[];

  dialog = false;

  get title() {
    let lang;
    switch (this.language) {
      case "x-php":
        lang = "PHP";
        break;
      case "html":
        lang = "HTML";
        break;
      case "javascript":
        lang = "JavaScript";
        break;
      case "css":
        lang = "CSS";
        break;
      default:
        lang = this.language;
    }

    return `Open ${lang} Editor`;
  }

  closeDialog() {
    this.dialog = false;
  }

  edit(newVal: string | null) {
    if (!newVal) {
      this.$emit("input", null);
      return;
    }

    this.$emit("input", newVal);
  }

  onInputRead(editor: Editor) {
    this.showHints(editor, {
      hints: this.twigVariables,
      separator: {
        start: "{{",
        end: "}}"
      }
    });

    this.showHints(editor, {
      formatter: val => val.toUpperCase(),
      hints: this.tags,
      onePerLine: true,
      separator: {
        start: "%%",
        end: "%%"
      }
    });
  }

  matchToken(line: string, separator: string, onePerLine = false) {
    const index = line.lastIndexOf(separator);
    if (index === -1) return null; // Bail out if not variables were found

    if (onePerLine) {
      const regexp = new RegExp(separator, "g");
      const found = line.match(regexp);

      if (found) {
        const numOfOccurrences = found.length;
        if (numOfOccurrences > 1) return;
      }
    }

    const token = line.substring(index);
    const word = token.replace(separator, "").trim();
    const end = index + token.length;

    return {
      token,
      word,
      index,
      start: index,
      end
    };
  }

  filterList(
    variables: string[],
    separator: { start: string; end: string },
    keyword: string,
    formatter?: (val: string) => string
  ) {
    const noop = (val: string) => ` ${val} `;
    const fn = formatter ? formatter : noop;

    return variables
      .filter(variable => {
        // Filter results by doing case insensitive match on name
        const haystack = variable.toLowerCase();
        return haystack.includes(keyword.toLowerCase());
      })
      .sort((a, b) => {
        // Sort results by matching name with keyword position in name
        const valA = a.toLowerCase();
        const valB = b.toLowerCase();
        const search = keyword.toLowerCase();

        if (valA.indexOf(search) > valB.indexOf(search)) {
          return 1;
        } else if (valA.indexOf(search) < valB.indexOf(search)) {
          return -1;
        } else {
          if (a > b) return 1;
          else return -1;
        }
      })
      .map(variable => ({
        // Format to CodeMirror expected input
        text: separator.start + fn(variable) + separator.end,
        displayText: variable
      }));
  }

  showHints(
    editor: Editor,
    options: {
      hints: string[];
      separator: { start: string; end: string };
      formatter?: (val: string) => string;
      onePerLine?: boolean;
    }
  ) {
    const { hints, separator, formatter, onePerLine } = options;

    const cursor = editor.getDoc().getCursor();
    const line = editor.getLine(cursor.line);
    const match = this.matchToken(line, separator.start, onePerLine);

    if (!match) return;

    const list = this.filterList(hints, separator, match.word, formatter);

    const hintOptions = {
      hint: () => ({
        list,
        from: CodeMirror.Pos(cursor.line, match.start),
        to: CodeMirror.Pos(cursor.line, match.end)
      }),
      completeSingle: false,
      closeCharacters: /[\s()[\]{};:>,]|(%{2})/
    };

    editor.showHint(hintOptions);
  }

  replaceCodeWithCodeBoxCall(code: string) {
    this.edit(code);
  }
}
